<script setup>
import {t} from "@/controllerLK/GlobusLocalization";
import {computed, ref, watch} from "vue";
import {useRoute, useRouter} from "vue-router";
import {useProjects} from "@/store/projects";
import LKProjectModal from "@/pages/LKPage/LKWindow/LKProjectWindow/LKProjectModal.vue";
import LKProjectCardNew from "@/pages/LKPage/LKWindow/LKProjectWindow/LKProjectCardNew.vue";
import LKProjectCard from "@/pages/LKPage/LKWindow/LKProjectWindow/LKProjectCard.vue";

const projectStore = useProjects();
const route = useRoute();
const router = useRouter();

const isEdit = ref(false);
const projectSearchString = ref("");
const isArchivePage = ref(null);

// Отслеживание изменения родительского маршрута (projects/archive).
watch(() => route.matched[1]?.name, (newName, oldName) => {
  if (newName === "projects" || newName === "archive") {
    isArchivePage.value = newName === "archive";
    // Загружаем проекты только при смене раздела (projects <-> archive)
    if (oldName !== newName) {
      projectStore.getProjects(isArchivePage.value);
    }
  }
}, { immediate: true });

const projectList = computed(() => {
  // Список архивных проектов (Только где пользователь владелец) или список активных проектов.
  const sourceProjects = isArchivePage.value
      ? projectStore.archivedProjects.filter(project => project.is_owner)
      : projectStore.projects;

  // Если заполнено значение поисковой строки - фильтруем.
  if (projectSearchString.value.length > 0) {
    return sourceProjects.filter(project =>
        project.name.toLowerCase().includes(projectSearchString.value.toLowerCase())
    );
  }

  return sourceProjects;
});


/* Модальные окна. CRUD **/

// Модальное окно создания/редактирования проекта
const selectedProject = ref(null);
const isShowProjectModal = computed(() => route.meta.modal === true);

const openCreateProjectModal = () => {
  isEdit.value = false;
  router.push({name: "project-new"});
};
const openEditProjectModal = (project) => {
  selectedProject.value = project;
  isEdit.value = true;
  router.push({
    name: isArchivePage.value ? "archive-project-edit" : "project-edit",
    params: {projectId: project.id}
  });
};
const closeProjectModal = () => router.push({
  name: isArchivePage.value ? "archive" : "projects"
});

// Модальное окно удаления проекта.
const isShowDeleteProjectModal = ref(false);
const deleteProjectModalToggle = (project, show) => {
  // Очищаем активный проект, только если модальное окно проекта закрыто.
  if (!isShowProjectModal.value) {
    selectedProject.value = project;
  }
  isShowDeleteProjectModal.value = show;
};

// Удаление проекта
const deleteProject = () => {
  projectStore.deleteProject(selectedProject.value.id, isArchivePage.value, selectedProject.value.is_owner);
  isShowDeleteProjectModal.value = false;
  isShowProjectModal.value = false;
};

// Список пустых карточек для сетки.
const getEmptyCardCount = computed(() => {
  if (projectList.value.length === 0) return 3;
  if (projectList.value.length === 1) return 2;
  if (projectList.value.length === 2) return 1;
  return 0;
});
</script>

<template>
  <div class="window-wrapper scroll">
    <div class="header">
      <div class="header__title">
        {{ t('lk_project_window.title') }}
      </div>
      <InputSearchTertiary
        v-model="projectSearchString"
        class="header__search"
        :placeholder="t('lk_project_window_search.placeholder')"
      />
    </div>

    <div class="body scroll">
      <LKProjectCardNew
        v-if="!isArchivePage"
        class="project-card"
        @click="openCreateProjectModal"
      />

      <LKProjectCard
        v-for="project in projectList"
        :key="project.id"
        :project="project"
        :is-archive="isArchivePage"
        @edit-project="projectId => openEditProjectModal(projectId)"
        @delete-project="val => deleteProjectModalToggle(val, true)"
      />
      <div
        v-for="card in getEmptyCardCount"
        :key="card"
      />
    </div>

    <LKProjectModal
      v-if="isShowProjectModal"
      :is-edit="isEdit"
      :is-archive="isArchivePage"
      :project="selectedProject"
      @close-modal="closeProjectModal"
      @show-delete-project-modal="isShowDeleteProjectModal = true"
    />

    <Modal
      v-if="isShowDeleteProjectModal"
      :title="t('lk_delete_project_modal.title')"
      teleport="#appMain"
      :confirm-text="t('lk_delete_project_modal.button.delete')"
      :cancel-text="t('lk_delete_project_modal.button.cancel')"
      @confirm="deleteProject"
      @cancel="deleteProjectModalToggle(null, false)"
    >
      {{ t('lk_delete_project_modal.message', selectedProject?.name) }}?
    </Modal>
  </div>
</template>

<style scoped lang="scss">
.window-wrapper {
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 33px;
  height: 100%;

  .header {
    display: flex;
    justify-content: space-between;
    gap: 5px;

    &__title {
      font-size: 28px;
      font-weight: 400;
      text-wrap: nowrap;
    }
  }

  .body {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
    grid-column-gap: 12px;
    grid-row-gap: 12px;
    padding-bottom: 30px;
    grid-auto-rows: max-content;
    height: 100%;
  }

  .footer {
    position: absolute;
    bottom: 0;
    right: 0;
    display: flex;
    justify-content: flex-end;
  }
}

.project-scroll {
  padding-right: 10px;
  overflow-y: auto;
  &::-webkit-scrollbar {
    width: 7px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background-color: #1A64D433;
    border-radius: 30px;
  }
}
</style>
